import React, { useState } from 'react';
import { X, Plus, Check, Pencil, Trash2, ChevronRight, LogOut } from 'lucide-react';
import { usePet } from '../context/PetContext';
import { useAuth } from '../context/AuthContext';
import { useTrialStatus } from '../hooks/useTrialStatus';
import { PET_COLORS, PET_SPECIES_EMOJI, PetSpecies, PetProfile } from '../types';
import { cn } from '../lib/utils';
import { SubscriptionGateModal } from './SubscriptionGateModal';
import DogIcon from './Custom Icons/dog_icon';
import CatIcon from './Custom Icons/cat_icon';
import RabbitIcon from './Custom Icons/rabbit_icon';
import ChickenIcon from './Custom Icons/chicken_icon';
import HorseIcon from './Custom Icons/horse_icon';
import GoatIcon from './Custom Icons/goat_icon';
import SheepIcon from './Custom Icons/sheep_icon';
import PigIcon from './Custom Icons/pig_icon';
import ReptileIcon from './Custom Icons/reptile_icon';
import OtherAnimalIcon from './Custom Icons/otheranimal_icon';

// ─── Species selector ─────────────────────────────────────────────────────────

const SPECIES_LABELS: Record<PetSpecies, string> = {
  dog: 'Dog',
  cat: 'Cat',
  rabbit: 'Rabbit',
  chicken: 'Chicken',
  horse: 'Horse',
  goat: 'Goat',
  sheep: 'Sheep',
  pig: 'Pig',
  reptile: 'Reptile',
  otheranimal: 'Other Animal',
  // legacy values
  bird: 'Bird',
  fish: 'Fish',
  other: 'Other',
};

const SPECIES_ICONS: Partial<Record<PetSpecies, React.ComponentType<React.SVGProps<SVGSVGElement>>>> = {
  dog: DogIcon,
  cat: CatIcon,
  rabbit: RabbitIcon,
  chicken: ChickenIcon,
  horse: HorseIcon,
  goat: GoatIcon,
  sheep: SheepIcon,
  pig: PigIcon,
  reptile: ReptileIcon,
  otheranimal: OtherAnimalIcon,
  // legacy fallback mappings
  bird: ChickenIcon,
  fish: ReptileIcon,
  other: OtherAnimalIcon,
};

const SPECIES_OPTIONS: { value: PetSpecies; label: string }[] = [
  { value: 'dog', label: SPECIES_LABELS.dog },
  { value: 'cat', label: SPECIES_LABELS.cat },
  { value: 'rabbit', label: SPECIES_LABELS.rabbit },
  { value: 'chicken', label: SPECIES_LABELS.chicken },
  { value: 'horse', label: SPECIES_LABELS.horse },
  { value: 'goat', label: SPECIES_LABELS.goat },
  { value: 'sheep', label: SPECIES_LABELS.sheep },
  { value: 'pig', label: SPECIES_LABELS.pig },
  { value: 'reptile', label: SPECIES_LABELS.reptile },
  { value: 'otheranimal', label: SPECIES_LABELS.otheranimal },
];

export function SpeciesGlyph({ species, className, color }: { species: PetSpecies; className?: string; color: string }) {
  const Icon = SPECIES_ICONS[species];
  if (Icon) {
    return (
      <Icon
        className={cn(
          'pet-species-svg text-current [&_.cls-1]:fill-current [&_.cls-2]:fill-current [&_.cls-3]:fill-current [&_path]:fill-current [&_ellipse]:fill-current [&_circle]:fill-current',
          className
        )}
        style={{ color }}
      />
    );
  }
  return (
    <span className={className} style={{ color }}>
      {PET_SPECIES_EMOJI[species]}
    </span>
  );
}

// ─── Pet Form (create / edit) ─────────────────────────────────────────────────

interface PetFormProps {
  initial?: Partial<PetProfile>;
  onSave: (data: { name: string; species: PetSpecies; breed: string; color: string }) => Promise<void>;
  onCancel: () => void;
}

function PetForm({ initial, onSave, onCancel }: PetFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [species, setSpecies] = useState<PetSpecies>(initial?.species ?? 'dog');
  const [breed, setBreed] = useState(initial?.breed ?? '');
  const [color, setColor] = useState(initial?.color ?? PET_COLORS[0]);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({ name: name.trim(), species, breed: breed.trim(), color });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1.5">Pet Name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Luna"
          maxLength={30}
          className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 outline-none focus:border-primary-brown transition-colors"
        />
      </div>

      {/* Breed */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1.5">Breed <span className="font-normal text-gray-400">(optional)</span></label>
        <input
          type="text"
          value={breed}
          onChange={e => setBreed(e.target.value)}
          placeholder="e.g. Labrador Retriever"
          maxLength={40}
          className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 outline-none focus:border-primary-brown transition-colors"
        />
      </div>

      {/* Species */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1.5">Icon</label>
        <div className="grid grid-cols-5 gap-2">
          {SPECIES_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setSpecies(opt.value)}
              aria-label={opt.label}
              title={opt.label}
              className={cn(
                'flex items-center justify-center h-12 rounded-xl border transition-all',
                species === opt.value
                  ? 'border-primary-brown bg-primary-brown/5 text-primary-brown'
                  : 'border-gray-100 bg-white text-gray-500 hover:border-gray-200'
              )}
            >
              <SpeciesGlyph species={opt.value} className="w-7 h-7" color={color} />
            </button>
          ))}
        </div>
      </div>

      {/* Color */}
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1.5">Profile Colour</label>
        <div className="flex gap-2 flex-wrap">
          {PET_COLORS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              style={{ background: c }}
              className={cn(
                'w-8 h-8 rounded-full transition-all',
                color === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'
              )}
            />
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!name.trim() || saving}
          className="flex-1 py-2.5 rounded-xl bg-primary-brown text-white text-sm font-semibold disabled:opacity-40 hover:opacity-90 transition-opacity"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ─── Pet Card ─────────────────────────────────────────────────────────────────

interface PetCardProps {
  pet: PetProfile;
  isActive: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  canDelete: boolean;
  isShared?: boolean;
  canLeave?: boolean;
  onLeave?: () => void;
}

function PetCard({ pet, isActive, onSelect, onEdit, onDelete, canDelete, isShared, canLeave, onLeave }: PetCardProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3.5 rounded-2xl border transition-all cursor-pointer',
        isActive ? 'border-primary-brown/30 bg-primary-brown/5' : 'border-gray-100 bg-white hover:bg-gray-50'
      )}
      onClick={onSelect}
    >
      {/* Color dot + emoji */}
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-lg shrink-0"
        style={{ background: pet.color + '22' }}
      >
        <SpeciesGlyph species={pet.species} className="w-6 h-6" color={pet.color} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-800 truncate">{pet.name}</p>
        <p className="text-xs text-gray-400 truncate capitalize">
          {pet.breed ? `${pet.breed}` : SPECIES_LABELS[pet.species]}
        </p>
      </div>

      {/* Active check */}
      {isActive && (
        <div
          className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
          style={{ background: pet.color }}
        >
          <Check size={11} className="text-white" strokeWidth={3} />
        </div>
      )}

      {/* Edit / Delete (own pets only) or Shared badge + Leave button */}
      {isShared ? (
        <div className="flex items-center gap-1 ml-1" onClick={e => e.stopPropagation()}>
          <span className="text-[10px] font-semibold text-gray-400 italic">Shared</span>
          {canLeave && (
            <button
              type="button"
              onClick={onLeave}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-error-red hover:bg-error-red/5 transition-colors"
              title="Leave shared pet"
            >
              <LogOut size={13} />
            </button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-1 ml-1" onClick={e => e.stopPropagation()}>
          <button
            type="button"
            onClick={onEdit}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-primary-brown hover:bg-primary-brown/10 transition-colors"
          >
            <Pencil size={13} />
          </button>
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-error-red hover:bg-error-red\/5 transition-colors"
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main PetSwitcher ─────────────────────────────────────────────────────────

interface PetSwitcherProps {
  onClose: () => void;
}

type PanelView =
  | { mode: 'list' }
  | { mode: 'add' }
  | { mode: 'edit'; pet: PetProfile };

export function PetSwitcher({ onClose }: PetSwitcherProps) {
  const { pets, activePetId, setActivePet, createPet, updatePet, deletePet, leaveSharedPet, petLimit, canCreatePet, isActivePetSubordinate } = usePet();
  const { firebaseUser } = useAuth();
  const { hasActiveSubscription } = useTrialStatus();
  const currentUid = firebaseUser?.uid ?? '';
  const ownPetCount = pets.filter(p => p.user_id === currentUid).length;
  const limitLabel = Number.isFinite(petLimit) ? String(petLimit) : 'Unlimited';
  const [view, setView] = useState<PanelView>({ mode: 'list' });
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [gateModalOpen, setGateModalOpen] = useState(false);

  const handleSelect = (petId: string) => {
    setActivePet(petId);
    onClose();
  };

  const handleCreate = async (data: { name: string; species: PetSpecies; breed: string; color: string }) => {
    if (!canCreatePet) return;
    try {
      const id = await createPet(data);
      setActivePet(id);
      setView({ mode: 'list' });
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message === 'CO_CARER_SUBSCRIPTION_REQUIRED') {
        setGateModalOpen(true);
        setView({ mode: 'list' });
      } else {
        // Error already shown by createPet via toast
        setView({ mode: 'list' });
      }
    }
  };

  const handleAddPetClick = () => {
    // Co-carers without subscription cannot add pets
    if (isActivePetSubordinate && !hasActiveSubscription) {
      setGateModalOpen(true);
      return;
    }
    setView({ mode: 'add' });
  };

  const handleUpdate = async (petId: string, data: { name: string; species: PetSpecies; breed: string; color: string }) => {
    await updatePet(petId, data);
    setView({ mode: 'list' });
  };

  const handleDelete = async (petId: string) => {
    const pet = pets.find(p => p.id === petId);
    if (pet && pet.user_id !== currentUid) {
      await leaveSharedPet(petId);
    } else {
      await deletePet(petId);
    }
    setConfirmDelete(null);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200] bg-black/20 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Panel — bottom sheet on mobile, dropdown on desktop */}
      <div className={cn(
        'fixed z-[201] bg-white shadow-2xl',
        // Mobile: full-width bottom sheet
        'inset-x-0 bottom-0 rounded-t-3xl max-h-[85dvh] overflow-y-auto',
        // Desktop: dropdown anchored to top bar
        'sm:inset-x-auto sm:bottom-auto sm:top-[60px] sm:right-4 sm:w-[360px] sm:rounded-3xl sm:max-h-[80dvh]'
      )}>
        {/* Handle / Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-50">
          <div>
            <div className="w-8 h-1 bg-gray-200 rounded-full mx-auto mb-3 sm:hidden" />
            <h2 className="text-base font-black text-gray-800">
              {view.mode === 'add' ? 'Add a Pet' : view.mode === 'edit' ? 'Edit Pet' : 'My Pets'}
            </h2>
            {view.mode === 'list' && pets.length > 0 && (
              <p className="text-xs text-gray-400 mt-0.5">Tap a pet to switch to it</p>
            )}
          </div>
          <button
            type="button"
            onClick={view.mode === 'list' ? onClose : () => setView({ mode: 'list' })}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {/* ── List view ── */}
          {view.mode === 'list' && (
            <>
              <p className="text-xs text-gray-400">
                {ownPetCount}/{limitLabel} pets used
              </p>

              {pets.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center space-y-3">
                  <div className="w-14 h-14 mx-auto rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                    <Plus size={22} className="text-gray-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">No pet profiles yet</p>
                    <p className="text-xs text-gray-400 mt-1">Create a pet profile to start tracking care, reminders, and notes.</p>
                  </div>
                </div>
              ) : (
                pets.map(pet => {
                  const isShared = pet.user_id !== currentUid;
                  return (
                  <div key={pet.id}>
                    <PetCard
                      pet={pet}
                      isActive={pet.id === activePetId}
                      onSelect={() => handleSelect(pet.id)}
                      onEdit={() => setView({ mode: 'edit', pet })}
                      onDelete={() => setConfirmDelete(pet.id)}
                      canDelete={!isShared}
                      isShared={isShared}
                      canLeave={isShared}
                      onLeave={() => setConfirmDelete(pet.id)}
                    />
                    {/* Inline delete / leave confirm */}
                    {confirmDelete === pet.id && (
                      <div className="mt-2 p-3 bg-error-red\/5 border border-error-red\/10 rounded-xl flex items-center gap-3">
                        <p className="flex-1 text-xs text-error-red font-medium">
                          {isShared
                            ? `Leave ${pet.name}? You'll be removed as a co-carer. The pet's data won't be deleted.`
                            : `Remove ${pet.name}? This can't be undone. All pet data will be permanently deleted.`
                          }
                        </p>
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(null)}
                          className="text-xs text-gray-500 font-semibold px-2 py-1 rounded-lg hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(pet.id)}
                          className="text-xs text-white font-semibold px-3 py-1.5 rounded-lg bg-error-red hover:bg-error-red"
                        >
                          {isShared ? 'Leave' : 'Remove'}
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })
              )}

              {/* Add Pet button */}
              <button
                type="button"
                onClick={handleAddPetClick}
                disabled={!canCreatePet}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl border border-dashed text-sm font-semibold transition-all',
                  canCreatePet
                    ? 'border-gray-200 text-gray-500 hover:border-primary-brown hover:text-primary-brown hover:bg-primary-brown/5'
                    : 'border-gray-200 text-gray-300 cursor-not-allowed'
                )}
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                  <Plus size={18} className="text-gray-400" />
                </div>
                Add a pet
                <ChevronRight size={16} className="ml-auto" />
              </button>

              {!canCreatePet && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                  You have reached your pet profile limit for this plan.
                </p>
              )}
            </>
          )}

          {/* ── Add view ── */}
          {view.mode === 'add' && (
            <PetForm
              onSave={handleCreate}
              onCancel={() => setView({ mode: 'list' })}
            />
          )}

          {/* ── Edit view ── */}
          {view.mode === 'edit' && (
            <PetForm
              initial={view.pet}
              onSave={data => handleUpdate(view.pet.id, data)}
              onCancel={() => setView({ mode: 'list' })}
            />
          )}
        </div>

        {/* Safe area spacer */}
        <div className="h-safe-area-inset-bottom sm:hidden pb-6" />
      </div>

      {/* Subscription gate modal for co-carers */}
      <SubscriptionGateModal
        open={gateModalOpen}
        onClose={() => setGateModalOpen(false)}
        action="add_pet"
      />
    </>
  );
}
